import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import Shell from '../dashboard/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import FeatureGate from '../../components/FeatureGate';
import './NotificationPreferences.css';

// Helper function for ordinal suffixes (1st, 2nd, 3rd, etc.)
const getOrdinalSuffix = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

const NotificationPreferences = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsageLoading, setAiUsageLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    aiNotificationsEnabled: false, // Master toggle - must be explicitly enabled
    enableDailySummary: false,
    enableWeeklyReport: false,
    enableMonthlyReport: false,
    enableInventoryAlerts: false,
    enableChurnWarnings: false,
    enableRevenueForecasts: false,
    lowStockThreshold: 10,
    churnDaysThreshold: 30,
    emailNotifications: false,
    dashboardNotifications: false,
    // Daily Summary
    dailySummaryTime: '18:00',
    // Weekly Report
    weeklyReportDay: 'MONDAY',
    weeklyReportTime: '09:00',
    // Monthly Report
    monthlyReportDay: 1,
    monthlyReportTime: '09:00',
    // Inventory Alerts
    inventoryAlertFrequency: 'daily',
    inventoryAlertTime: '09:00',
    inventoryAlertDay: 'MONDAY',
    // Churn Warnings
    churnWarningFrequency: 'weekly',
    churnWarningTime: '09:00',
    churnWarningDay: 'MONDAY',
    // Revenue Forecasts
    revenueForecastFrequency: 'weekly',
    revenueForecastTime: '09:00',
    revenueForecastDay: 'MONDAY',
    revenueForecastMonthDay: 1
  });

  useEffect(() => {
    fetchPreferences();
    fetchAiUsage();
  }, []);

  const fetchAiUsage = async () => {
    try {
      const { data } = await axios.get('/ai/merchant/usage/comprehensive');
      if (data?.success && data.data) {
        setAiUsage(data.data);
      }
    } catch (error) {
      // 403 is expected for FREE merchants (FeatureGate handles the UI);
      // don't surface as a user-facing error.
      if (error.response?.status !== 403 && error.response?.status !== 401) {
        console.error('Error fetching AI usage:', error);
      }
    } finally {
      setAiUsageLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const { data } = await axios.get('/notifications/preferences');
      if (data?.success && data.preferences) {
        setPreferences(data.preferences);
      }
    } catch (error) {
      // 403 = FREE tier merchant; FeatureGate already shows the upgrade
      // prompt around this whole page, so no need to toast a duplicate
      // error. Same for 401 (auth interceptor handles redirect).
      const status = error.response?.status;
      if (status !== 403 && status !== 401) {
        console.error('Error fetching preferences:', error);
        toast?.error('Failed to load preferences');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const { data } = await axios.put('/notifications/preferences', preferences);
      if (data?.success) {
        toast?.success('Notification preferences saved successfully!');
      } else {
        throw new Error(data?.error || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast?.error(error.response?.data?.error || error.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    setPreferences(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Shell>
        <div className="notification-preferences-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading preferences...</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <FeatureGate featureId="notifications.ai" upgradeMessage="Upgrade to access AI-powered notification preferences and automated insights">
      <div className="notification-preferences-container">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1>AI Notification Preferences</h1>
            <p className="page-subtitle">Customize when and how you receive automated insights and alerts</p>
          </div>
        </div>

        {/* Master Toggle Section */}
        <Card className="preferences-card master-toggle-card">
          <div className="master-toggle-section">
            <div className="master-toggle-content">
              <div className="master-toggle-info">
                <h2 className="master-toggle-title">Enable AI Notifications</h2>
                <p className="master-toggle-description">
                  {preferences.aiNotificationsEnabled
                    ? 'AI notifications are enabled. Configure your preferences below.'
                    : 'Turn on AI notifications to receive automated insights, reports, and alerts. You can customize individual preferences after enabling.'}
                </p>
              </div>
              <label className="toggle-switch toggle-switch-large">
                <input
                  type="checkbox"
                  checked={preferences.aiNotificationsEnabled}
                  onChange={() => handleToggle('aiNotificationsEnabled')}
                />
                <span className="toggle-slider toggle-slider-large"></span>
              </label>
            </div>
            {!preferences.aiNotificationsEnabled && (
              <div className="master-toggle-warning">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span>AI notifications are currently disabled. Enable them to start receiving automated insights.</span>
              </div>
            )}
          </div>
        </Card>

        {/* AI Usage Overview Section */}
        <Card className="preferences-card ai-usage-card">
          <div className="card-header">
            <h2>AI Usage Overview</h2>
            <p className="section-description">Track your AI credit usage across different time periods</p>
          </div>

          {aiUsageLoading ? (
            <div className="ai-usage-loading">Loading usage data...</div>
          ) : aiUsage?.quota?.isBlocked ? (
            <div className="ai-blocked-warning">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
              </svg>
              <div>
                <strong>AI Access Blocked</strong>
                <p>Your AI access has been temporarily restricted. Please contact support for assistance.</p>
              </div>
            </div>
          ) : aiUsage ? (
            <>
              {/* Usage Cards */}
              <div className="ai-usage-grid">
                <div className="ai-usage-card-item">
                  <div className="ai-usage-label">Today</div>
                  <div className="ai-usage-value">{aiUsage.usage?.daily?.queries || 0}</div>
                  <div className="ai-usage-subtext">queries used</div>
                  {aiUsage.remaining && !aiUsage.quota?.isUnlimited && (
                    <div className="ai-usage-remaining">
                      {aiUsage.remaining.daily?.queries || 0} remaining
                    </div>
                  )}
                </div>
                <div className="ai-usage-card-item">
                  <div className="ai-usage-label">This Week</div>
                  <div className="ai-usage-value">{aiUsage.usage?.weekly?.queries || 0}</div>
                  <div className="ai-usage-subtext">queries used</div>
                  {aiUsage.remaining && !aiUsage.quota?.isUnlimited && (
                    <div className="ai-usage-remaining">
                      {aiUsage.remaining.weekly?.queries || 0} remaining
                    </div>
                  )}
                </div>
                <div className="ai-usage-card-item">
                  <div className="ai-usage-label">This Month</div>
                  <div className="ai-usage-value">{aiUsage.usage?.monthly?.queries || 0}</div>
                  <div className="ai-usage-subtext">queries used</div>
                  {aiUsage.remaining && !aiUsage.quota?.isUnlimited && (
                    <div className="ai-usage-remaining">
                      {aiUsage.remaining.monthly?.queries || 0} remaining
                    </div>
                  )}
                </div>
                <div className="ai-usage-card-item">
                  <div className="ai-usage-label">This Year</div>
                  <div className="ai-usage-value">{aiUsage.usage?.yearly?.queries || 0}</div>
                  <div className="ai-usage-subtext">queries used</div>
                  {aiUsage.remaining && !aiUsage.quota?.isUnlimited && (
                    <div className="ai-usage-remaining">
                      {aiUsage.remaining.yearly?.queries || 0} remaining
                    </div>
                  )}
                </div>
              </div>

              {/* Quota Info */}
              {aiUsage.quota?.isUnlimited ? (
                <div className="ai-unlimited-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span>Unlimited AI Usage</span>
                </div>
              ) : (
                <div className="ai-quota-info">
                  <div className="ai-quota-title">Your AI Quota Limits</div>
                  <div className="ai-quota-grid">
                    <div>Daily: <strong>{aiUsage.quota?.daily?.queries || 100}</strong> queries</div>
                    <div>Weekly: <strong>{aiUsage.quota?.weekly?.queries || 500}</strong> queries</div>
                    <div>Monthly: <strong>{aiUsage.quota?.monthly?.queries || 2000}</strong> queries</div>
                    <div>Yearly: <strong>{aiUsage.quota?.yearly?.queries || 20000}</strong> queries</div>
                  </div>
                </div>
              )}

              {/* Usage by Type */}
              {aiUsage.usageByType && aiUsage.usageByType.length > 0 && (
                <div className="ai-usage-by-type">
                  <div className="ai-usage-type-title">Usage by Type (Last 30 Days)</div>
                  <div className="ai-usage-type-grid">
                    {aiUsage.usageByType.map((item, idx) => (
                      <div key={idx} className="ai-usage-type-item">
                        <span className="ai-usage-type-name">{item.type?.replace(/_/g, ' ')}</span>
                        <span className="ai-usage-type-count">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="ai-usage-empty">No AI usage data available yet. Start using AI features to see your usage here.</div>
          )}
        </Card>

        {/* Automated Reports Section */}
        <Card className="preferences-card" style={{ opacity: preferences.aiNotificationsEnabled ? 1 : 0.6 }}>
          <div className="card-header">
            <h2>Automated Reports</h2>
            <p className="section-description">Receive AI-generated business insights on your schedule</p>
          </div>

          <div className="preference-items">
            {/* Daily Summary */}
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Daily Sales Summary</div>
                <p className="preference-description">End-of-day recap with AI insights and performance metrics</p>
              </div>
              <div className="preference-controls">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.enableDailySummary}
                    onChange={() => handleToggle('enableDailySummary')}
                    disabled={!preferences.aiNotificationsEnabled}
                  />
                  <span className="toggle-slider"></span>
                </label>
                {preferences.enableDailySummary && (
                  <input
                    type="time"
                    value={preferences.dailySummaryTime}
                    onChange={(e) => handleChange('dailySummaryTime', e.target.value)}
                    className="time-input"
                  />
                )}
              </div>
            </div>

            {/* Weekly Report */}
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Weekly Performance Report</div>
                <p className="preference-description">Comprehensive business analysis with actionable recommendations</p>
              </div>
              <div className="preference-controls">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.enableWeeklyReport}
                    onChange={() => handleToggle('enableWeeklyReport')}
                    disabled={!preferences.aiNotificationsEnabled}
                  />
                  <span className="toggle-slider"></span>
                </label>
                {preferences.enableWeeklyReport && (
                  <div className="schedule-controls">
                    <select
                      value={preferences.weeklyReportDay}
                      onChange={(e) => handleChange('weeklyReportDay', e.target.value)}
                      className="day-select"
                    >
                      <option value="MONDAY">Monday</option>
                      <option value="TUESDAY">Tuesday</option>
                      <option value="WEDNESDAY">Wednesday</option>
                      <option value="THURSDAY">Thursday</option>
                      <option value="FRIDAY">Friday</option>
                      <option value="SATURDAY">Saturday</option>
                      <option value="SUNDAY">Sunday</option>
                    </select>
                    <input
                      type="time"
                      value={preferences.weeklyReportTime}
                      onChange={(e) => handleChange('weeklyReportTime', e.target.value)}
                      className="time-input"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Monthly Report */}
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Monthly Report</div>
                <p className="preference-description">Deep dive into monthly performance trends and forecasts</p>
              </div>
              <div className="preference-controls">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.enableMonthlyReport}
                    onChange={() => handleToggle('enableMonthlyReport')}
                    disabled={!preferences.aiNotificationsEnabled}
                  />
                  <span className="toggle-slider"></span>
                </label>
                {preferences.enableMonthlyReport && (
                  <div className="schedule-controls">
                    <div className="schedule-label">Day</div>
                    <select
                      value={preferences.monthlyReportDay}
                      onChange={(e) => handleChange('monthlyReportDay', parseInt(e.target.value))}
                      className="day-select"
                    >
                      {[...Array(28)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}{getOrdinalSuffix(i + 1)}</option>
                      ))}
                    </select>
                    <input
                      type="time"
                      value={preferences.monthlyReportTime}
                      onChange={(e) => handleChange('monthlyReportTime', e.target.value)}
                      className="time-input"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Smart Alerts Section */}
        <Card className="preferences-card" style={{ opacity: preferences.aiNotificationsEnabled ? 1 : 0.6 }}>
          <div className="card-header">
            <h2>Smart Alerts</h2>
            <p className="section-description">Get notified about important business events automatically</p>
          </div>

          <div className="preference-items">
            {/* Inventory Alerts */}
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Inventory Alerts</div>
                <p className="preference-description">Notifications for low stock and out-of-stock products</p>
              </div>
              <div className="preference-controls">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.enableInventoryAlerts}
                    onChange={() => handleToggle('enableInventoryAlerts')}
                    disabled={!preferences.aiNotificationsEnabled}
                  />
                  <span className="toggle-slider"></span>
                </label>
                {preferences.enableInventoryAlerts && (
                  <>
                    <div className="threshold-input-group">
                      <label className="threshold-label">Alert when stock ≤</label>
                      <input
                        type="number"
                        min="0"
                        value={preferences.lowStockThreshold}
                        onChange={(e) => handleChange('lowStockThreshold', parseInt(e.target.value))}
                        className="threshold-input"
                      />
                      <span className="threshold-unit">units</span>
                    </div>
                    <div className="schedule-controls">
                      <select
                        value={preferences.inventoryAlertFrequency}
                        onChange={(e) => handleChange('inventoryAlertFrequency', e.target.value)}
                        className="frequency-select"
                      >
                        <option value="immediate">Immediate</option>
                        <option value="daily">Daily digest</option>
                        <option value="weekly">Weekly digest</option>
                      </select>
                      {preferences.inventoryAlertFrequency !== 'immediate' && (
                        <>
                          {preferences.inventoryAlertFrequency === 'weekly' && (
                            <select
                              value={preferences.inventoryAlertDay}
                              onChange={(e) => handleChange('inventoryAlertDay', e.target.value)}
                              className="day-select"
                            >
                              <option value="MONDAY">Monday</option>
                              <option value="TUESDAY">Tuesday</option>
                              <option value="WEDNESDAY">Wednesday</option>
                              <option value="THURSDAY">Thursday</option>
                              <option value="FRIDAY">Friday</option>
                              <option value="SATURDAY">Saturday</option>
                              <option value="SUNDAY">Sunday</option>
                            </select>
                          )}
                          <input
                            type="time"
                            value={preferences.inventoryAlertTime}
                            onChange={(e) => handleChange('inventoryAlertTime', e.target.value)}
                            className="time-input"
                          />
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Churn Warnings */}
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Customer Churn Warnings</div>
                <p className="preference-description">Identify customers at risk of churning with AI analysis</p>
              </div>
              <div className="preference-controls">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.enableChurnWarnings}
                    onChange={() => handleToggle('enableChurnWarnings')}
                    disabled={!preferences.aiNotificationsEnabled}
                  />
                  <span className="toggle-slider"></span>
                </label>
                {preferences.enableChurnWarnings && (
                  <>
                    <div className="threshold-input-group">
                      <label className="threshold-label">No orders for</label>
                      <input
                        type="number"
                        min="1"
                        value={preferences.churnDaysThreshold}
                        onChange={(e) => handleChange('churnDaysThreshold', parseInt(e.target.value))}
                        className="threshold-input"
                      />
                      <span className="threshold-unit">days</span>
                    </div>
                    <div className="schedule-controls">
                      <select
                        value={preferences.churnWarningFrequency}
                        onChange={(e) => handleChange('churnWarningFrequency', e.target.value)}
                        className="frequency-select"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                      {preferences.churnWarningFrequency === 'weekly' && (
                        <select
                          value={preferences.churnWarningDay}
                          onChange={(e) => handleChange('churnWarningDay', e.target.value)}
                          className="day-select"
                        >
                          <option value="MONDAY">Monday</option>
                          <option value="TUESDAY">Tuesday</option>
                          <option value="WEDNESDAY">Wednesday</option>
                          <option value="THURSDAY">Thursday</option>
                          <option value="FRIDAY">Friday</option>
                          <option value="SATURDAY">Saturday</option>
                          <option value="SUNDAY">Sunday</option>
                        </select>
                      )}
                      <input
                        type="time"
                        value={preferences.churnWarningTime}
                        onChange={(e) => handleChange('churnWarningTime', e.target.value)}
                        className="time-input"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Revenue Forecasts */}
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Revenue Forecasts</div>
                <p className="preference-description">AI-powered revenue predictions for upcoming periods</p>
              </div>
              <div className="preference-controls">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.enableRevenueForecasts}
                    onChange={() => handleToggle('enableRevenueForecasts')}
                    disabled={!preferences.aiNotificationsEnabled}
                  />
                  <span className="toggle-slider"></span>
                </label>
                {preferences.enableRevenueForecasts && (
                  <div className="schedule-controls">
                    <select
                      value={preferences.revenueForecastFrequency}
                      onChange={(e) => handleChange('revenueForecastFrequency', e.target.value)}
                      className="frequency-select"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    {preferences.revenueForecastFrequency === 'weekly' && (
                      <select
                        value={preferences.revenueForecastDay}
                        onChange={(e) => handleChange('revenueForecastDay', e.target.value)}
                        className="day-select"
                      >
                        <option value="MONDAY">Monday</option>
                        <option value="TUESDAY">Tuesday</option>
                        <option value="WEDNESDAY">Wednesday</option>
                        <option value="THURSDAY">Thursday</option>
                        <option value="FRIDAY">Friday</option>
                        <option value="SATURDAY">Saturday</option>
                        <option value="SUNDAY">Sunday</option>
                      </select>
                    )}
                    {preferences.revenueForecastFrequency === 'monthly' && (
                      <select
                        value={preferences.revenueForecastMonthDay}
                        onChange={(e) => handleChange('revenueForecastMonthDay', parseInt(e.target.value))}
                        className="day-select"
                      >
                        {[...Array(28)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}{getOrdinalSuffix(i + 1)}</option>
                        ))}
                      </select>
                    )}
                    <input
                      type="time"
                      value={preferences.revenueForecastTime}
                      onChange={(e) => handleChange('revenueForecastTime', e.target.value)}
                      className="time-input"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Delivery Methods Section */}
        <Card className="preferences-card" style={{ opacity: preferences.aiNotificationsEnabled ? 1 : 0.6 }}>
          <div className="card-header">
            <h2>Delivery Methods</h2>
            <p className="section-description">Choose how you want to receive notifications</p>
          </div>

          <div className="preference-items">
            {/* Email Notifications */}
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Email Notifications</div>
                <p className="preference-description">Receive detailed reports and alerts via email</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.emailNotifications}
                  onChange={() => handleToggle('emailNotifications')}
                  disabled={!preferences.aiNotificationsEnabled}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {/* Dashboard Notifications */}
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Dashboard Notifications</div>
                <p className="preference-description">Show notifications in your merchant dashboard</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.dashboardNotifications}
                  onChange={() => handleToggle('dashboardNotifications')}
                  disabled={!preferences.aiNotificationsEnabled}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </Card>

        {/* Action Footer */}
        <div className="preferences-footer">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="save-button"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>
      </FeatureGate>
    </Shell>
  );
};

export default NotificationPreferences;
